<template>
    <div>
        <CustomDialog
            :value="visible"
            @input="handleDialogInput"
            :defaultTitle="modalTitle"
            width="600px"
            heightClass="ppv2-max-h-[85vh]"
            bgHeaderClass="!ppv2-bg-white dark:!ppv2-bg-dark-popup"
            customClassActions="!ppv2-justify-end"
        >
            <template #content>
                <!-- Le modal ne porte plus QUE le formulaire : la recherche d'un client existant
                 s'est déplacée dans le champ du devis, celui qui ouvre ce popup. La garder ici
                 aurait fait deux recherches concurrentes pour le même geste. -->
                <div class="single-pane-layout ppv2-rounded-lg">
                    <div class="pane pane-form client-form-card">
                        <div class="client-form-body custom-scrollbar">
                            <!-- TYPE DE CLIENT — deux boutons au lieu d'une liste déroulante. Les
                             valeurs restent celles de `computedClientTypeOptions` : seul le
                             véhicule change, `newClient.type` reçoit la même chose qu'avant. -->
                            <div class="segmented ppv2-bg-black ppv2-bg-opacity-5" role="group">
                                <button
                                    v-for="option in computedClientTypeOptions"
                                    :key="option.value"
                                    type="button"
                                    class="segmented-item poppinsMedium"
                                    :class="{ 'is-active': newClient.type === option.value }"
                                    :aria-pressed="newClient.type === option.value ? 'true' : 'false'"
                                    @click="newClient.type = option.value"
                                >
                                    {{ option.label }}
                                </button>
                            </div>

                            <!-- Les champs professionnels occupent la première ligne après le type. -->
                            <template v-if="newClient.type === 1 && !selectedCompany">
                                <div class="field-grid">
                                    <div class="field-block company-lookup">
                                        <div class="company-field-control">
                                            <CustomInput
                                                :label="$t('devisV3.clientSelector.socialReason')"
                                                v-model="newClient.social_reason"
                                                :placeholder="$t('devisV3.clientSelector.placeholders.socialReason')"
                                                classInputAdd="company-search-input"
                                                customLabelClass="poppinsMedium ppv2-text-black"
                                                :customSizeLabel="12.5"
                                                @input="onCompanyQueryInput($event, 'social_reason')"
                                                @focus="focusCompanySearch('social_reason')"
                                                @blur="blurCompanySearch('social_reason')"
                                            />
                                            <Search :size="15" class="company-search-icon" />
                                        </div>
                                        <ul
                                            v-if="companySearchOpen && companySearchField === 'social_reason'"
                                            class="company-results custom-scrollbar"
                                        >
                                            <li v-if="companyLoading" class="company-result company-result--message">
                                                {{ $t('devisV3.clientSelector.companySearching') }}
                                            </li>
                                            <li
                                                v-for="company in companyResults"
                                                :key="company.siret || company.siren"
                                                class="company-result"
                                                @pointerdown.prevent.stop="applyCompany(company)"
                                            >
                                                <span class="company-name poppinsMedium">{{ company.social_reason }}</span>
                                                <span class="company-meta">{{ companyResultMeta(company) }}</span>
                                            </li>
                                            <li
                                                v-if="!companyLoading && companySearched && companyResults.length === 0"
                                                class="company-result company-result--message"
                                            >
                                                {{ $t('devisV3.clientSelector.companyNoResult') }}
                                            </li>
                                            <li class="company-results-source">
                                                {{ $t('devisV3.clientSelector.companyDataSource') }}
                                                <a
                                                    href="https://recherche-entreprises.api.gouv.fr/"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {{ $t('devisV3.clientSelector.companyDataSourceName') }}
                                                </a>
                                            </li>
                                        </ul>
                                    </div>
                                    <div class="field-block company-lookup">
                                        <div class="company-field-control">
                                            <CustomInput
                                                :label="$t('devisV3.clientSelector.identificationNumber')"
                                                v-model="newClient.siret"
                                                :placeholder="$t('devisV3.clientSelector.placeholders.identificationNumber')"
                                                classInputAdd="company-search-input"
                                                customLabelClass="poppinsMedium ppv2-text-primary"
                                                :customSizeLabel="12.5"
                                                @input="onCompanyQueryInput($event, 'siret')"
                                                @focus="focusCompanySearch('siret')"
                                                @blur="blurCompanySearch('siret')"
                                            />
                                            <Search :size="15" class="company-search-icon" />
                                        </div>
                                        <ul
                                            v-if="companySearchOpen && companySearchField === 'siret'"
                                            class="company-results custom-scrollbar"
                                        >
                                            <li v-if="companyLoading" class="company-result company-result--message">
                                                {{ $t('devisV3.clientSelector.companySearching') }}
                                            </li>
                                            <li
                                                v-for="company in companyResults"
                                                :key="company.siret || company.siren"
                                                class="company-result"
                                                @pointerdown.prevent.stop="applyCompany(company)"
                                            >
                                                <span class="company-name poppinsMedium">{{ company.social_reason }}</span>
                                                <span class="company-meta">{{ companyResultMeta(company) }}</span>
                                            </li>
                                            <li
                                                v-if="!companyLoading && companySearched && companyResults.length === 0"
                                                class="company-result company-result--message"
                                            >
                                                {{ $t('devisV3.clientSelector.companyNoResult') }}
                                            </li>
                                            <li class="company-results-source">
                                                {{ $t('devisV3.clientSelector.companyDataSource') }}
                                                <a
                                                    href="https://recherche-entreprises.api.gouv.fr/"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {{ $t('devisV3.clientSelector.companyDataSourceName') }}
                                                </a>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </template>

                            <div v-if="newClient.type === 1 && selectedCompany" class="company-summary">
                                <div class="company-summary-row company-summary-row--name">
                                    <div
                                        class="company-inline-value company-inline-value--name"
                                        @click="editCompanyField('social_reason')"
                                    >
                                        <input
                                            v-if="editingCompanyField === 'social_reason'"
                                            ref="companyNameInput"
                                            v-model="newClient.social_reason"
                                            class="company-inline-input company-name-title poppinsSemiBold"
                                            :style="inlineCompanyInputStyle(newClient.social_reason)"
                                            @blur="finishCompanyEdit('social_reason')"
                                            @keydown.enter.prevent="$event.target.blur()"
                                            @click.stop
                                        />
                                        <div v-else class="company-hover-badge company-name-badge">
                                            <span class="company-name-title poppinsSemiBold">{{ newClient.social_reason }}</span>
                                            <Pencil :size="13" class="company-inline-pencil" />
                                        </div>
                                    </div>
                                    <button type="button" class="company-change-button poppinsMedium" @click="changeCompany">
                                        {{ $t('devisV3.clientSelector.changeCompany') }}
                                    </button>
                                </div>
                                <div class="company-summary-row">
                                    <span class="company-summary-label">SIREN :</span>
                                    <div
                                        class="company-inline-value company-inline-value--siren"
                                        @click="editCompanyField('siren')"
                                    >
                                        <input
                                            v-if="editingCompanyField === 'siren'"
                                            ref="companySirenInput"
                                            v-model="companySiren"
                                            class="company-inline-input"
                                            :style="inlineCompanyInputStyle(companySiren)"
                                            @blur="finishCompanyEdit('siren')"
                                            @keydown.enter.prevent="$event.target.blur()"
                                            @click.stop
                                        />
                                        <div v-else class="company-hover-badge company-siren-badge">
                                            <span class="company-summary-value poppinsRegular">{{ formattedSiren }}</span>
                                            <Pencil :size="13" class="company-inline-pencil" />
                                        </div>
                                    </div>
                                </div>
                                <div class="company-summary-row company-summary-row--address">
                                    <span class="company-summary-label">{{ $t('devisV3.clientSelector.address') }} :</span>
                                    <div
                                        class="company-inline-value company-inline-value--address"
                                        @click="editCompanyField('address')"
                                    >
                                        <textarea
                                            v-if="editingCompanyField === 'address'"
                                            ref="companyAddressInput"
                                            v-model="newClient.address"
                                            class="company-inline-input company-inline-textarea"
                                            :rows="Math.max(2, Math.ceil((newClient.address || '').length / 65))"
                                            @blur="finishCompanyEdit('address')"
                                            @click.stop
                                        ></textarea>
                                        <div v-else class="company-hover-badge company-address-badge">
                                            <span class="company-address-text">{{ newClient.address }}</span>
                                            <Pencil :size="13" class="company-inline-pencil" />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="field-block">
                                <label class="field-label">{{ $t('devisV3.clientSelector.civilityLabel') }}</label>
                                <div class="choice-row">
                                    <label
                                        v-for="option in computedCivilityOptions"
                                        :key="option.value"
                                        class="choice-card"
                                        :class="{ 'is-active': newClient.civility === option.value }"
                                    >
                                        <input type="radio" :value="option.value" v-model="newClient.civility" />
                                        <span class="choice-dot"></span>
                                        <span class="choice-text poppinsMedium">{{ civilityShortLabel(option) }}</span>
                                    </label>
                                </div>
                            </div>

                            <!-- NOM / PRÉNOM sur une ligne. Le nom n'est exigé que pour un
                             particulier : l'astérisque suit la validation, il ne l'invente pas. -->
                            <div class="field-grid">
                                <div class="field-block">
                                    <CustomInput
                                        :label="$t('devisV3.clientSelector.lastName')"
                                        v-model="newClient.last_name"
                                        :placeholder="$t('devisV3.clientSelector.placeholders.lastName')"
                                        :errorText="errors.last_name"
                                        @input="clearError('last_name')"
                                        classInputAdd="!ppv2-rounded-xl"
                                        customLabelClass="poppinsMedium ppv2-text-black"
                                        :customSizeLabel="12.5"
                                    />
                                </div>
                                <div class="field-block">
                                    <CustomInput
                                        :label="$t('devisV3.clientSelector.firstName')"
                                        v-model="newClient.first_name"
                                        :placeholder="$t('devisV3.clientSelector.placeholders.firstName')"
                                        :errorText="errors.first_name"
                                        @input="clearError('first_name')"
                                        classInputAdd="!ppv2-rounded-xl"
                                        customLabelClass="poppinsMedium ppv2-text-black"
                                        :customSizeLabel="12.5"
                                    />
                                </div>
                            </div>

                            <!-- EMAIL / TÉLÉPHONE sur une ligne. L'ancien séparateur « ou » disait
                             qu'un seul des deux suffisait, alors que `createNewClient` les exige
                             TOUS LES DEUX : la mise côte à côte reflète la règle réelle. -->
                            <div class="field-grid">
                                <div class="field-block">
                                    <CustomInput
                                        :label="$t('devisV3.clientSelector.email')"
                                        v-model="newClient.email"
                                        :placeholder="$t('devisV3.clientSelector.placeholders.email')"
                                        :errorText="errors.email"
                                        @input="clearError('email')"
                                        classInputAdd="!ppv2-rounded-xl"
                                        customLabelClass="poppinsMedium ppv2-text-black"
                                        :customSizeLabel="12.5"
                                    />
                                </div>
                                <div class="field-block">
                                    <!-- Indicatif et numéro sont saisis — et stockés — séparément.
                                     Coller un numéro international ne casse rien : `splitPastedPhone`
                                     détache l'indicatif et le range dans le sélecteur. -->
                                    <div class="phone-group">
                                        <select
                                            v-model="newClient.phone_country_code"
                                            class="phone-code poppinsMedium ppv2-text-black ppv2-text-sm"
                                        >
                                            <option v-for="code in dialCodes" :key="code" :value="code">
                                                {{ dialCodeFlag(code) }} {{ code }}
                                            </option>
                                        </select>
                                        <CustomInput
                                            :label="$t('devisV3.clientSelector.phone')"
                                            v-model="newClient.phone"
                                            :placeholder="phonePlaceholder"
                                            :errorText="errors.phone"
                                            @input="onPhoneInput"
                                            classInputAdd="!ppv2-rounded-xl"
                                            customLabelClass="poppinsMedium ppv2-text-black"
                                            :customSizeLabel="12.5"
                                            class="phone-number"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div v-if="newClient.type !== 1 || !selectedCompany" class="field-block address-block">
                                <div class="address-autocomplete-control">
                                    <CustomInput
                                        :label="$t('devisV3.clientSelector.address')"
                                        v-model="newClient.address"
                                        :placeholder="$t('devisV3.clientSelector.placeholders.address')"
                                        :errorText="errors.address"
                                        @input="onAddressTyped"
                                        @focus="onAddressFocus"
                                        @blur="blurAddressSearch"
                                        classInputAdd="!ppv2-rounded-xl"
                                        customLabelClass="poppinsMedium ppv2-text-black"
                                        :customSizeLabel="12.5"
                                    />

                                    <ul v-if="addressSuggestions.length > 0" class="address-results custom-scrollbar">
                                        <li
                                            v-for="suggestion in addressSuggestions"
                                            :key="suggestion.place_id || suggestion.description"
                                            class="address-result"
                                            @pointerdown.prevent.stop="applyAddressSuggestion(suggestion)"
                                        >
                                            <span class="address-main poppinsMedium">{{
                                                suggestion.main_text || suggestion.description
                                            }}</span>
                                            <span v-if="suggestion.secondary_text" class="address-secondary">{{
                                                suggestion.secondary_text
                                            }}</span>
                                        </li>
                                        <li class="address-results-source">
                                            {{ $t('devisV3.clientSelector.addressDataSource') }}
                                        </li>
                                    </ul>
                                </div>

                                <p v-if="addressMessage" class="address-hint">
                                    {{ addressMessage }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
            <template #actions>
                <BaseButton
                    variant="outline"
                    @click="close"
                    customClass="!ppv2-rounded-xl !ppv2-min-h-[40px] ppv2-min-w-[110px] ppv2-bg-white dark:ppv2-bg-dark-popup"
                    :customHeight="40"
                >
                    {{ $t('common.cancel') }}
                </BaseButton>
                <BaseButton
                    variant="primary"
                    @click="createNewClient"
                    :loading="loading"
                    customClass="!ppv2-rounded-xl !ppv2-min-h-[40px] ppv2-min-w-[110px]"
                    :customHeight="40"
                >
                    {{ $t('devisV3.clientSelector.validate') }}
                </BaseButton>
            </template>
        </CustomDialog>
        <CustomDialog
            :value="showDiscardConfirmation"
            @input="handleDiscardDialogInput"
            :defaultTitle="$t('devisV3.clientSelector.discardConfirmation.title')"
            width="640px"
            bgHeaderClass="!ppv2-bg-white dark:!ppv2-bg-dark-popup"
            customClassActions="!ppv2-justify-end"
        >
            <template #content>
                <p class="discard-confirmation-message">
                    {{ $t('devisV3.clientSelector.discardConfirmation.message') }}
                </p>
            </template>
            <template #actions>
                <BaseButton
                    variant="outline"
                    @click="discardAndClose"
                    customClass="!ppv2-rounded-full !ppv2-min-h-[44px] ppv2-bg-white dark:ppv2-bg-dark-popup"
                    :customHeight="45"
                >
                    {{ $t('devisV3.clientSelector.discardConfirmation.leaveWithoutSaving') }}
                </BaseButton>
                <BaseButton
                    variant="purple"
                    @click="saveAndClose"
                    :loading="loading"
                    customClass="!ppv2-rounded-full !ppv2-min-h-[44px]"
                    :customHeight="45"
                >
                    {{ $t('devisV3.clientSelector.discardConfirmation.saveAndExit') }}
                </BaseButton>
            </template>
        </CustomDialog>
    </div>
</template>

<script>
import axios from 'axios'
import lodash from 'lodash'
import { Search, Pencil } from 'lucide-vue'

// Longueur minimale d'une recherche d'entreprise. Imposée par l'annuaire lui-même, qui
// répond 400 en dessous — et reprise à l'identique côté serveur (CompanyLookupService).
const COMPANY_MIN_CHARS = 3
import addressAutocompleteMixin from '../../../mixins/addressAutocompleteMixin'
import { classifyClientPrefill, CLIENT_PREFILL_MIN_CHARS } from '../../../utils/clientPrefill'
import CustomDialog from '../../Shared/CustomDialog.vue'
import BaseButton from '../../Shared/BaseButton.vue'
import CustomInput from '../../Inputs/CustomInput.vue'
import normalizeFontSize from '../../../utils/normalizeFontSize.js'

export default {
    name: 'ClientSelectorModal',
    // Suggestions d'adresse : même logique que la fiche client, factorisée pour qu'une
    // correction profite aux deux écrans.
    mixins: [addressAutocompleteMixin],
    components: {
        CustomDialog,
        BaseButton,
        CustomInput,
        Search,
        Pencil,
    },
    props: {
        visible: {
            type: Boolean,
            required: true,
        },
        // Nom déjà saisi dans la recherche du devis. Purement un pré-remplissage de confort :
        // le formulaire reste modifiable et aucun champ ne devient obligatoire pour autant.
        prefillName: {
            type: String,
            default: '',
        },
        // Fiche à ouvrir directement en MODIFICATION. `null` ouvre le formulaire en création.
        clientId: {
            type: Number,
            default: null,
        },
    },
    data() {
        return {
            loading: false,
            // Recherche d'entreprise via l'API Recherche d'Entreprises (data.gouv.fr).
            companyQuery: '',
            companyResults: [],
            companyLoading: false,
            companySearched: false,
            companySearchOpen: false,
            companySearchField: null,
            selectedCompany: false,
            editingCompanyField: null,
            companySiren: '',
            // Génération de la dernière recherche lancée : une réponse plus ancienne (« dec »)
            // ne doit pas écraser la liste d'une frappe plus récente (« decathlon »). Le
            // debounce annule les timers, pas les requêtes déjà parties.
            companyReqId: 0,
            // Identifiant de la fiche ouverte en modification, `null` en création.
            editingClientId: null,
            loadingClient: false,
            // Indicatifs proposés. La liste reste courte — les pays d'où viennent les
            // clients — et le champ accepte de toute façon un numéro collé avec le sien.
            dialCodes: ['+33', '+32', '+41', '+352', '+49', '+34', '+39', '+44', '+216', '+212', '+213', '+1'],
            newClient: {
                type: 0,
                civility: '',
                first_name: '',
                last_name: '',
                email: '',
                phone_country_code: '+33',
                phone: '',
                address: '',
                // Repli quand Google ne propose rien : la fiche reste enregistrable.
                city: '',
                postal_code: '',
                country: '',
                social_reason: '',
                siret: '',
                fonction: '',
                tva: '',
            },
            errors: {},
            showDiscardConfirmation: false,
            initialFormSnapshot: null,
        }
    },
    computed: {
        internalVisible: {
            get() {
                return this.visible
            },
            set(val) {
                this.$emit('update:visible', val)
            },
        },
        /**
         * Titre du modal : il nomme ce que le formulaire fait, création ou modification.
         *
         * `modalTitle` de l'ancien volet double (« Sélectionner ou créer… ») ne décrit plus
         * l'écran : on ne sélectionne plus ici, la recherche vit dans le champ du devis.
         */
        modalTitle() {
            return this.editingClientId
                ? this.$t('devisV3.clientSelector.editClient')
                : this.$t('devisV3.clientSelector.newClient')
        },
        hasUnsavedChanges() {
            return this.initialFormSnapshot !== null && this.getFormSnapshot() !== this.initialFormSnapshot
        },
        computedClientTypeOptions() {
            return [
                { value: 0, label: this.$t('devisV3.clientSelector.types.individual') },
                { value: 1, label: this.$t('devisV3.clientSelector.types.company') },
            ]
        },
        phonePlaceholder() {
            const countryByCode = {
                '+33': 'fr',
                '+32': 'be',
                '+41': 'ch',
                '+352': 'lu',
                '+49': 'de',
                '+34': 'es',
                '+39': 'it',
                '+44': 'gb',
                '+216': 'tn',
                '+212': 'ma',
                '+213': 'dz',
                '+1': 'us',
            }

            return this.$t(
                `devisV3.clientSelector.placeholders.phoneByCountry.${countryByCode[this.newClient.phone_country_code] || 'fr'}`
            )
        },
        /**
         * Civilités proposées à la CRÉATION depuis le devis : M. et Mme.
         *
         * « Autre » a été retiré de ce formulaire. La valeur reste acceptée partout ailleurs
         * (caisse, agenda, filtres client) et les clients déjà enregistrés avec elle ne sont
         * pas touchés : seul le choix offert ici se réduit.
         */
        computedCivilityOptions() {
            return [
                { value: 'Homme', label: this.$t('devisV3.clientSelector.civility.man') },
                { value: 'Femme', label: this.$t('devisV3.clientSelector.civility.woman') },
            ]
        },
        formattedSiren() {
            const raw = String(this.companySiren || this.newClient.siret || '').replace(/\D/g, '')
            if (raw.length === 9) {
                return raw.replace(/(\d{3})(?=\d)/g, '$1 ')
            }
            if (raw.length === 14) {
                return `${raw.slice(0, 3)} ${raw.slice(3, 6)} ${raw.slice(6, 9)} ${raw.slice(9)}`
            }
            return this.companySiren || this.newClient.siret || ''
        },
    },
    watch: {
        visible(newVal) {
            if (newVal) {
                // À l'OUVERTURE seulement : rouvrir le popup sur une fiche en cours de
                // modification ne doit pas réécrire ce que l'utilisateur y a saisi.
                if (this.clientId) {
                    this.selectClient({ id: this.clientId })
                } else {
                    this.applyPrefillName()
                    this.captureInitialFormSnapshot()
                }

                return
            }

            // Reset when modal is closed (e.g. from backdrop)
            this.resetNewClient()
        },
    },
    created() {
        this.debouncedCompanySearch = lodash.debounce(this.runCompanySearch, 300)
    },
    beforeDestroy() {
        if (this.debouncedCompanySearch && typeof this.debouncedCompanySearch.cancel === 'function') {
            this.debouncedCompanySearch.cancel()
        }
    },
    methods: {
        normalizeFontSize,
        /**
         * Libellé COURT d'une civilité : « M. » et « Mme » comme sur la maquette, plutôt que
         * « Homme » / « Femme ».
         *
         * Purement visuel — la VALEUR envoyée reste celle de `computedCivilityOptions`, donc
         * ce que le backend recevait déjà en `gender`. Les deux clés `civility.m` et
         * `civility.mme` existaient déjà, elles servaient à l'ancienne liste de résultats.
         */
        civilityShortLabel(option) {
            if (option.value === 'Homme') return this.$t('devisV3.clientSelector.civility.m')
            if (option.value === 'Femme') return this.$t('devisV3.clientSelector.civility.mme')

            // Repli conservé : si une civilité est réintroduite dans les options, elle
            // s'affiche avec son propre libellé plutôt que de sortir vide.
            return option.label
        },
        dialCodeFlag(code) {
            const flags = {
                '+33': '🇫🇷',
                '+32': '🇧🇪',
                '+41': '🇨🇭',
                '+352': '🇱🇺',
                '+49': '🇩🇪',
                '+34': '🇪🇸',
                '+39': '🇮🇹',
                '+44': '🇬🇧',
                '+216': '🇹🇳',
                '+212': '🇲🇦',
                '+213': '🇩🇿',
                '+1': '🇺🇸',
            }

            return flags[code] || ''
        },
        close() {
            if (this.hasUnsavedChanges) {
                this.showDiscardConfirmation = true
                return
            }

            this.performClose()
        },
        handleDiscardDialogInput(value) {
            this.showDiscardConfirmation = value
            if (!value && this.visible) {
                this.$nextTick(() => {
                    document.body.style.overflow = 'hidden'
                })
            }
        },
        handleDialogInput(value) {
            if (value) {
                this.$emit('update:visible', true)
                return
            }

            this.close()
        },
        getFormSnapshot() {
            return JSON.stringify({
                client: this.newClient,
                companySiren: this.companySiren,
                selectedCompany: this.selectedCompany,
            })
        },
        captureInitialFormSnapshot() {
            this.$nextTick(() => {
                this.initialFormSnapshot = this.getFormSnapshot()
            })
        },
        performClose() {
            this.showDiscardConfirmation = false
            this.$emit('update:visible', false)
            this.resetNewClient()
            this.initialFormSnapshot = null
        },
        discardAndClose() {
            this.performClose()
        },
        async saveAndClose() {
            await this.createNewClient()
        },
        /**
         * Frappe dans le champ entreprise — la requête est différée.
         *
         * Trois caractères minimum : c'est l'annuaire qui l'impose, il répond 400 en dessous.
         * Le serveur applique la même règle ; ce contrôle-ci évite seulement l'aller-retour.
         */
        onCompanyQueryInput(value, field = this.companySearchField || 'social_reason') {
            this.companySearchField = field
            this.companyQuery = String(value ?? this.companyQuery ?? '')
            this.companySearchOpen = true
            // Toute frappe invalide une requête encore en vol : sans ce bump, la réponse d'un
            // terme abandonné (« decathlon ») arriverait avec `reqId === companyReqId` et
            // repeuplerait le dropdown sous la saisie courante (« de »).
            this.companyReqId++
            this.companySearched = false
            this.companyResults = []

            if (this.companyQuery.trim().length < COMPANY_MIN_CHARS) {
                this.companyLoading = false
                this.debouncedCompanySearch?.cancel?.()

                return
            }

            this.companyLoading = true
            this.debouncedCompanySearch?.()
        },
        focusCompanySearch(field) {
            const value = field === 'siret' ? this.newClient.siret : this.newClient.social_reason
            this.onCompanyQueryInput(value, field)
        },
        blurCompanySearch(field) {
            setTimeout(() => {
                if (this.companySearchField === field) {
                    this.companySearchOpen = false
                }
            }, 200)
        },
        companyResultMeta(company) {
            const siren = company.siren || String(company.siret || '').slice(0, 9)
            const location = [company.postal_code, company.city].filter(Boolean).join(' ')

            return [siren ? `SIREN ${siren}` : '', location].filter(Boolean).join(' · ')
        },
        inlineCompanyInputStyle(value) {
            return { width: `min(${Math.max(String(value || '').length + 1, 8)}ch, 100%)` }
        },
        editCompanyField(field) {
            this.editingCompanyField = field
            this.$nextTick(() => {
                const refs = {
                    social_reason: this.$refs.companyNameInput,
                    siren: this.$refs.companySirenInput,
                    address: this.$refs.companyAddressInput,
                }
                refs[field]?.focus()
            })
        },
        finishCompanyEdit(field) {
            if (field === 'siren') {
                this.newClient.siret = this.companySiren
            }

            this.editingCompanyField = null
        },
        changeCompany() {
            this.selectedCompany = false
            this.editingCompanyField = null
            this.companySearchOpen = false
            this.companyResults = []
            this.companySearched = false
            this.companyQuery = ''
        },
        async runCompanySearch() {
            const term = (this.companyQuery || '').trim()

            if (term.length < COMPANY_MIN_CHARS) return

            // Jeton de cette recherche : à l'arrivée de la réponse, on ne touche à rien si une
            // recherche plus récente a été lancée entre-temps.
            const reqId = ++this.companyReqId

            try {
                const { data } = await axios.get('/back-office/v3/devis/companies/search', { params: { q: term } })

                if (reqId !== this.companyReqId) {
                    return
                }

                this.companyResults = data.data || []
            } catch (error) {
                // Une panne du répertoire ne doit rien bloquer : on laisse la saisie manuelle.
                console.error('Company lookup failed:', error)

                if (reqId !== this.companyReqId) {
                    return
                }

                this.companyResults = []
            } finally {
                // Seule la dernière recherche pilote l'état d'affichage : une réponse dépassée
                // ne doit ni éteindre le spinner d'une recherche en cours, ni marquer « fini ».
                if (reqId === this.companyReqId) {
                    this.companyLoading = false
                    this.companySearched = true
                }
            }
        },
        /**
         * Une entreprise choisie PRÉ-REMPLIT la fiche — elle ne la fige pas.
         *
         * Chaque champ reste modifiable ensuite, et rien n'est enregistré à ce stade. Le
         * formulaire n'ayant qu'une adresse libre, les lignes d'adresse du registre sont
         * recomposées.
         */
        applyCompany(company) {
            this.newClient.social_reason = company.social_reason || ''
            // Le SIRET est celui du SIÈGE ; le SIREN sert de repli pour une fiche qui n'en
            // aurait pas — l'annuaire le rend toujours, lui.
            this.newClient.siret = company.siret || company.siren || ''
            this.companySiren = company.siren || String(company.siret || '').slice(0, 9)
            // Numéro de TVA intracommunautaire : l'annuaire le calcule, l'ancien registre
            // ne le donnait pas. Le champ existait déjà et restait vide.
            this.newClient.tva = company.tva || ''
            this.newClient.city = company.city || ''
            this.newClient.postal_code = company.postal_code || ''
            this.newClient.country = company.country || ''
            this.newClient.address = [company.street, [company.postal_code, company.city].filter(Boolean).join(' ')]
                .filter(Boolean)
                .join(', ')

            this.companyResults = []
            this.companySearched = false
            this.companySearchOpen = false
            this.companyReqId++
            this.companyLoading = false
            this.debouncedCompanySearch?.cancel?.()
            this.selectedCompany = true
            this.editingCompanyField = null
            this.companyQuery = company.social_reason || ''
            this.clearError('social_reason')
        },
        /**
         * Saisie ou COLLAGE dans le champ numéro.
         *
         * Un numéro collé avec son indicatif — « +32 470… », « 0041 79… », « 0033 6… » — est
         * scindé : l'indicatif rejoint le sélecteur, le reste le champ. Sans ça, le back
         * recevrait un national commençant par « + » et le stockerait tel quel, l'indicatif
         * affiché dans le sélecteur devenant alors faux.
         */
        onPhoneInput() {
            this.clearError('phone')

            const raw = String(this.newClient.phone || '').replace(/[\s.\-()]/g, '')

            // « 00 » est la forme longue de « + ». On la ramène à « + » avant de comparer.
            const international = raw.startsWith('+') ? raw : raw.startsWith('00') ? '+' + raw.slice(2) : null

            if (!international) return

            // Les indicatifs les plus longs d'abord : « +33 » ne doit pas l'emporter sur « +352 ».
            const match = [...this.dialCodes].sort((a, b) => b.length - a.length).find((code) => international.startsWith(code))

            if (!match) return

            this.newClient.phone_country_code = match
            this.newClient.phone = international.slice(match.length)
        },
        onAddressTyped(value) {
            this.clearError('address')
            this.onAddressInput(value ?? this.newClient.address)
        },
        onAddressFocus() {
            if (String(this.newClient.address || '').trim().length >= 2) {
                this.onAddressInput(this.newClient.address)
            }
        },
        blurAddressSearch() {
            setTimeout(() => {
                this.clearAddressSuggestions()
            }, 200)
        },
        /**
         * Une suggestion choisie recopie SON TEXTE dans le champ — rien d'autre.
         *
         * L'adresse reste une simple chaîne en base : ni identifiant Google, ni découpage.
         * Les champs manuels se referment, la sélection valant adresse complète.
         */
        applyAddressSuggestion(suggestion) {
            this.newClient.address = suggestion.description || suggestion.main_text || ''
            this.clearAddressSuggestions()
            this.addressFallback = false
        },
        /**
         * Recopie le nom tapé dans la recherche du devis vers le formulaire de création.
         *
         * Le premier mot devient le nom, le RESTE le prénom, conformément à la saisie
         * « Nom, puis prénom » de la recherche. Un mot seul ne remplit que le nom.
         *
         * Aucun écrasement : le pré-remplissage ne s'applique qu'à un formulaire vierge, donc
         * ni sur une fiche ouverte en modification, ni par-dessus une saisie en cours.
         */
        applyPrefillName() {
            const term = (this.prefillName || '').trim()

            if (
                term.length < CLIENT_PREFILL_MIN_CHARS ||
                this.editingClientId ||
                this.newClient.first_name ||
                this.newClient.last_name
            ) {
                return
            }

            const prefillType = classifyClientPrefill(term)

            if (prefillType === 'email') {
                this.newClient.email = term
                return
            }

            if (prefillType === 'phone') {
                this.newClient.phone = term
                this.onPhoneInput()
                return
            }

            const compact = term.replace(/\D/g, '')
            if (prefillType === 'identifier') {
                this.newClient.type = 1
                this.newClient.siret = compact
                return
            }

            if (prefillType === 'company') {
                this.newClient.type = 1
                this.newClient.social_reason = term
                this.companyQuery = term
                this.onCompanyQueryInput()
                return
            }

            const parts = term.split(/\s+/).filter(Boolean)
            this.newClient.last_name = parts[0]
            this.newClient.first_name = parts.slice(1).join(' ')
        },
        resetNewClient() {
            this.editingClientId = null
            this.clearAddressSuggestions()
            this.addressFallback = false
            this.companyQuery = ''
            this.companyResults = []
            this.companySearched = false
            this.companySearchOpen = false
            this.companySearchField = null
            this.selectedCompany = false
            this.editingCompanyField = null
            this.companySiren = ''
            // Invalide une requête en vol ET coupe le spinner : sans ça, rouvrir le popup
            // laisserait la réponse de la recherche précédente repeupler le dropdown, et un
            // debounce armé puis tiré sur un champ vidé sortirait avant le `finally` de
            // runCompanySearch — « Recherche en cours… » resterait affiché indéfiniment.
            this.companyReqId++
            this.companyLoading = false
            this.debouncedCompanySearch?.cancel?.()
            this.newClient = {
                type: 0,
                civility: '',
                first_name: '',
                last_name: '',
                email: '',
                phone_country_code: '+33',
                phone: '',
                address: '',
                // Repli quand Google ne propose rien : la fiche reste enregistrable.
                city: '',
                postal_code: '',
                country: '',
                social_reason: '',
                siret: '',
                fonction: '',
                tva: '',
            }
            this.errors = {}
        },
        getState(field) {
            return this.errors[field] ? 'error' : 'default'
        },
        clearError(field) {
            if (this.errors[field]) {
                this.$delete(this.errors, field)
            }
        },
        // 500 ms : chaque frappe partirait sinon vers l'annuaire, limité à 7 appels/seconde.
        debouncedCompanySearch: lodash.debounce(function () {
            this.runCompanySearch()
        }, 500),
        /**
         * Charge une fiche EXISTANTE dans le formulaire, qui passe en modification.
         *
         * Appelée à l'ouverture quand le devis désigne un client (`clientId`). Le popup ne se
         * referme pas pour autant : c'est « Valider » qui enregistre les retouches éventuelles
         * ET rattache le client au devis.
         *
         * La fiche est RECHARGÉE entière : la suggestion qui a mené ici ne porte ni raison
         * sociale, ni SIRET, ni TVA, ni fonction, ni adresse. Pré-remplir depuis elle puis
         * enregistrer effacerait ces champs, que le formulaire n'aurait jamais vus.
         */
        async selectClient(client) {
            if (this.loadingClient) return

            this.loadingClient = true
            this.errors = {}

            try {
                const { data } = await axios.get(`/back-office/v3/devis/clients/${client.id}`)
                const fiche = data.data

                this.newClient = {
                    type: fiche.customer_type ?? 0,
                    civility: fiche.civility || '',
                    first_name: fiche.first_name || '',
                    last_name: fiche.last_name || '',
                    email: fiche.email || '',
                    phone_country_code: fiche.phone_country_code || '+33',
                    phone: fiche.phone || '',
                    address: fiche.street || '',
                    city: fiche.city || '',
                    postal_code: fiche.postal_code || '',
                    country: fiche.country || '',
                    social_reason: fiche.social_reason || '',
                    siret: fiche.siret || '',
                    fonction: fiche.fonction || '',
                    tva: fiche.tva || '',
                }
                this.companySiren = String(fiche.siret || '').slice(0, 9)
                this.selectedCompany = false
                this.editingClientId = fiche.id
                this.captureInitialFormSnapshot()
            } catch (error) {
                console.error('Error loading customer record:', error)
                this.$toast?.error(this.$t('devisV3.clientSelector.errors.loadError'))
            } finally {
                this.loadingClient = false
            }
        },
        async createNewClient() {
            this.errors = {}

            // AUCUN champ n'est obligatoire, et il n'y a AUCUN minimum : un formulaire
            // entièrement vide crée une fiche. Le serveur applique la même règle
            // (StoreClientRequest) — l'écran ne doit pas refuser ce qu'il enregistre.
            //
            // Le seul contrôle restant porte sur la FORME d'un email saisi, jamais sur sa
            // présence : le serveur le refuserait aussi
            // (`nullable|email`), autant l'éviter un aller-retour plus tôt. Ce n'est pas une
            // obligation de saisie — un email vide passe.
            if (this.newClient.email) {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
                if (!emailRegex.test(this.newClient.email)) {
                    this.$set(this.errors, 'email', this.$t('devisV3.clientSelector.errors.invalidEmail'))
                }
            }

            if (Object.keys(this.errors).length > 0) {
                this.$toast?.error(this.$t('devisV3.clientSelector.errors.formError'))
                return
            }

            this.loading = true
            try {
                // Mapping fields to match backend expectations (/back-office/user/add/calendar)
                const payload = {
                    customer_type: this.newClient.type,
                    gender: this.newClient.civility, // ApiManager uses gender
                    first_name: this.newClient.first_name,
                    last_name: this.newClient.last_name,
                    email: this.newClient.email,
                    phone: this.newClient.phone,
                    // Stocké à part : le back recompose `phone_number` à partir des deux.
                    phone_country_code: this.newClient.phone_country_code,
                    guest: 0,
                    street: this.newClient.address,
                    // Renseignés seulement si Google n'a rien proposé (cf. addressFallback).
                    city: this.newClient.city,
                    postal_code: this.newClient.postal_code,
                    country: this.newClient.country,
                    social_reason: this.newClient.social_reason,
                    siret: this.newClient.siret,
                    fonction: this.newClient.fonction,
                    tva: this.newClient.tva,
                }

                // Une fiche ouverte en modification part sur l'endpoint de mise à jour ; le
                // chemin de création la dupliquerait — et échouerait sur l'index unique de
                // l'email.
                const response = this.editingClientId
                    ? await axios.post('/back-office/v3/devis/update-client', {
                          ...payload,
                          client_id: this.editingClientId,
                      })
                    : await axios.post('/back-office/v3/devis/store-client', payload)

                if (isNaN(response.data)) {
                    // It returned errors object
                    throw { response: { data: response.data } }
                }

                this.$toast?.success(
                    this.$t(
                        this.editingClientId
                            ? 'devisV3.clientSelector.errors.updateSuccess'
                            : 'devisV3.clientSelector.errors.createSuccess'
                    )
                )
                // The response is the new user ID
                this.$emit('selected', {
                    id: response.data,
                    first_name: this.newClient.first_name,
                    last_name: this.newClient.last_name,
                    display_name:
                        this.newClient.type === 1
                            ? this.newClient.social_reason
                            : `${this.newClient.first_name || ''} ${this.newClient.last_name || ''}`.trim(),
                    is_professional: this.newClient.type === 1,
                    social_reason: this.newClient.social_reason,
                    siret: this.newClient.siret,
                    email: this.newClient.email,
                    phone: this.newClient.phone,
                    phone_number: [this.newClient.phone_country_code, this.newClient.phone].filter(Boolean).join(' '),
                    address: this.newClient.address,
                    city: this.newClient.city,
                    postal_code: this.newClient.postal_code,
                    country: this.newClient.country,
                    fonction: this.newClient.fonction,
                    tva: this.newClient.tva,
                })
                this.performClose()
            } catch (error) {
                console.error('Error creating customer:', error)
                const msg = error.response?.data?.errors
                    ? Object.values(error.response.data.errors)[0][0]
                    : this.$t('devisV3.clientSelector.errors.createError')
                this.$toast?.error(msg)
            } finally {
                this.loading = false
            }
        },
    },
}
</script>

<style scoped lang="scss">
.single-pane-layout {
    min-height: 0;
}

.pane {
    min-width: 0;
}

.discard-confirmation-message {
    margin: 0;
    color: #6b7480;
    font-size: 16px;
    line-height: 1.5;
}

/* ── Carte « Nouveau client » ────────────────────────────────────────────────
   Trois bandes : un en-tête, un corps qui défile seul, un pied détaché par un
   filet. Le fond est blanc, la teinte violette de l'ancien volet ayant disparu
   au profit des bordures. */
.client-form-card {
    width: 100%;
    display: flex;
    flex-direction: column;
    min-height: 0;
    border: 0;
    border-radius: 0;
}

.form-mode-reset {
    color: #7d2dbd;
    font-size: 12px;
    cursor: pointer;

    &:hover {
        text-decoration: underline;
    }
}

.client-form-head {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    padding: 16px 22px;
    border-bottom: 1px solid #eef1f5;
    font-size: 15px;
}

/* Recherche entreprise : les résultats se déplient sous le champ, dans le flux — un menu
   flottant serait masqué par le défilement du formulaire. */
.company-lookup {
    position: relative;
    padding-bottom: 4px;
}

.company-field-control {
    position: relative;
}

.company-search-icon {
    position: absolute;
    right: 11px;
    bottom: 13px;
    color: #98a1ad;
    pointer-events: none;
}

.company-field-control ::v-deep .company-search-input {
    padding-right: 34px;
}

.company-hint {
    margin-top: 4px;
    color: #1d1d1b;
    opacity: 0.5;
    font-size: 12px;
}

/* Suggestions d'adresse : même présentation que celles d'entreprise, pour que les deux
   listes du formulaire se lisent pareil. */
.address-block {
    position: relative;
}

.address-autocomplete-control {
    position: relative;
}

.address-hint {
    margin-top: 4px;
    color: #1d1d1b;
    opacity: 0.5;
    font-size: 12px;
}

.address-results {
    position: absolute;
    bottom: 42px;
    left: 0;
    z-index: 35;
    width: 100%;
    box-sizing: border-box;
    max-height: 190px;
    overflow-y: auto;
    margin: 0;
    padding: 0;
    border: 1px solid #e9edf3;
    border-radius: 10px;
    background: #fff;
    list-style: none;
    box-shadow: 0 12px 24px rgba(16, 24, 40, 0.12);
}

.address-result {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 9px 12px;
    cursor: pointer;
    transition: background 0.15s ease;

    & + .address-result {
        border-top: 1px solid #f4f6f9;
    }

    &:hover {
        background: #f7f4fb;
    }
}

.address-main {
    color: #1d1d1b;
    font-size: 13px;
}

.address-secondary {
    color: #1d1d1b;
    opacity: 0.55;
    font-size: 11.5px;
}

.address-results-source {
    padding: 8px 12px;
    border-top: 1px solid #eef1f5;
    color: #8791a0;
    font-size: 11px;
}

.company-results {
    position: absolute;
    top: calc(100% - 8px);
    left: 0;
    z-index: 30;
    width: 100%;
    box-sizing: border-box;
    max-height: 190px;
    overflow-y: auto;
    margin: 0;
    padding: 0;
    border: 1px solid #e9edf3;
    border-radius: 10px;
    background: #fff;
    list-style: none;
    box-shadow: 0 12px 24px rgba(16, 24, 40, 0.12);
}

.company-result {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 9px 12px;
    cursor: pointer;
    transition: background 0.15s ease;

    & + .company-result {
        border-top: 1px solid #f4f6f9;
    }

    &:hover {
        background: #f7f4fb;
    }
}

.company-result--message {
    color: #687386;
    cursor: default;
}

.company-results-source {
    padding: 8px 12px;
    border-top: 1px solid #eef1f5;
    color: #8791a0;
    font-size: 11px;

    a {
        color: #687386;
        text-decoration: underline;
    }
}

.company-name {
    color: #1d1d1b;
    font-size: 13px;
}

.company-meta {
    color: #1d1d1b;
    opacity: 0.55;
    font-size: 11.5px;
}

.company-summary {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 14px 16px;
    border: 1px solid #d8b4fe;
    border-radius: 12px;
    background: #fff;
}

.company-summary-row {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    color: #334155;
    font-size: 13.5px;
    line-height: 1.5;
}

.company-summary-row--name {
    justify-content: space-between;
    margin-bottom: 2px;
}

.company-name-title {
    font-size: 15px;
    color: #021832;
}

.company-change-button {
    flex: 0 0 auto;
    border: 0;
    padding: 0;
    background: transparent;
    color: #7d2dbd;
    font-size: 13px;
    cursor: pointer;

    &:hover {
        text-decoration: underline;
    }
}

.company-summary-label {
    flex: 0 0 auto;
    color: #94a3b8;
    font-family: 'Poppins Medium', sans-serif;
    font-size: 13px;
}

.company-summary-value {
    color: #334155;
    font-size: 13.5px;
}

.company-summary-row--address {
    align-items: center;
}

.company-inline-value {
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    min-width: 0;
}

.company-inline-value--address {
    flex: 1 1 auto;
}

.company-hover-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 3px 8px;
    border-radius: 8px;
    border: 1px solid transparent;
    color: #334155;
    font-size: 13.5px;
    transition:
        background 0.15s ease,
        border-color 0.15s ease;
    max-width: 100%;

    .company-address-text {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: normal;
    }

    .company-inline-pencil {
        flex: 0 0 auto;
        color: #7d2dbd;
        opacity: 0;
        transition: opacity 0.15s ease;
    }

    &:hover {
        background: #f3e8ff;
        border-color: #d8b4fe;

        .company-inline-pencil {
            opacity: 1;
        }
    }
}

.company-inline-input {
    box-sizing: border-box;
    max-width: 100%;
    min-width: 7ch;
    padding: 2px 6px;
    border: 1px solid #d8b4fe;
    border-radius: 6px;
    outline: none;
    background: #fff;
    color: inherit;
    font: inherit;
}

.company-inline-textarea {
    width: 100%;
    min-height: 44px;
    padding: 4px 8px;
    border: 1px solid #d8b4fe;
    border-radius: 8px;
    background: #fff;
    color: #334155;
    font-size: 13.5px;
    outline: none;
    resize: vertical;
}

.client-form-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 14px;
}

/* ── Segmented control : type de client ──────────────────────────────────── */

.segmented {
    display: inline-flex;
    gap: 6px;
    padding: 4px;
    border: 1px solid #e9edf3;
    border-radius: 12px;
}

.segmented-item {
    padding: 8px 20px;
    border: 1px solid transparent;
    border-radius: 9px;
    color: #1d1d1b;
    font-size: 13px;
    cursor: pointer;
    transition:
        background 0.18s ease,
        color 0.18s ease,
        border-color 0.18s ease;
}

.segmented-item:hover:not(.is-active) {
    border-color: #d9d0e6;
}

.segmented-item.is-active {
    background: #7d2dbd;
    border-color: #7d2dbd;
    color: #fff;
}

/* ── Champs ──────────────────────────────────────────────────────────────── */

/* Indicatif et numéro se lisent comme UN seul champ : mêmes bordure, rayon, rembourrage
   et opacité que `.input-container` de CustomInput. Sans cet alignement, le sélecteur natif
   paraîtrait rapporté à côté du champ. */
.phone-group {
    display: flex;
    align-items: flex-end;
    gap: 8px;

    /* CustomInput porte un `margin-bottom: 20px` qui décalerait le sélecteur vers le haut.
       On le neutralise ICI, dans le groupe, plutôt que de toucher au composant partagé. */
    ::v-deep .custom-input-wrapper {
        margin-bottom: 0;
    }

    .phone-number {
        flex: 1 1 auto;
        min-width: 0;
    }
}

.phone-code {
    flex: 0 0 90px;
    /* Valeurs reprises de `.input-container` (CustomInput) pour que les deux s'alignent. */
    border: 1px solid #9c9af880;
    border-radius: 12px;
    padding: 0 8px;
    height: 42px;
    line-height: 1.5;
    cursor: pointer;
    outline: none;
    /* Même opacité au repos que son voisin : sans elle, le sélecteur paraîtrait actif
       quand le champ ne l'est pas. */
    opacity: 0.7;
    transition:
        opacity 0.2s ease,
        border-color 0.2s ease;

    &:hover,
    &:focus {
        opacity: 1;
        border-color: #7d2dbd;
    }
}

.field-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
}

.field-block {
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-width: 0;

    ::v-deep .custom-input-wrapper {
        margin-bottom: 0;
        gap: 5px;
    }

    ::v-deep .input-container {
        height: 42px;
        border-radius: 10px;
    }
}

.field-label {
    color: #1d1d1b;
    opacity: 0.72;
    font-family: 'Poppins Medium', sans-serif;
    font-size: 12.5px;
}

.field-error {
    color: #d63c3c;
    font-size: 11.5px;
}

/* ── Civilité : radios présentées en cartes ──────────────────────────────── */

.choice-row {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
}

.choice-card {
    display: inline-flex;
    align-items: center;
    gap: 9px;
    min-width: 108px;
    height: 44px;
    box-sizing: border-box;
    padding: 0 13px;
    border: 1px solid #e0e5ec;
    border-radius: 11px;
    background: #fff;
    cursor: pointer;
    transition:
        border-color 0.18s ease,
        box-shadow 0.18s ease;

    /* La vraie radio reste dans le DOM — accessibilité et navigation clavier —
       mais c'est la pastille dessinée qui est visible. */
    input[type='radio'] {
        position: absolute;
        opacity: 0;
        width: 0;
        height: 0;
    }

    &:not(.is-active) .choice-dot {
        background: #fff;
    }
}

.choice-card:hover {
    border-color: #c9b6dd;
}

.choice-card.is-active {
    border-color: #7d2dbd;
    box-shadow: 0 0 0 3px rgba(125, 45, 189, 0.1);
}

.choice-dot {
    flex: 0 0 auto;
    width: 16px;
    height: 16px;
    border: 1.5px solid #c6cdd8;
    border-radius: 50%;
    transition:
        border-color 0.18s ease,
        box-shadow 0.18s ease;
}

.choice-card.is-active .choice-dot {
    border-color: #7d2dbd;
    border-width: 5px;
}

.choice-text {
    font-size: 13.5px;
    color: #1d1d1b;
}

.choice-card input[type='radio']:focus-visible + .choice-dot {
    box-shadow: 0 0 0 3px rgba(125, 45, 189, 0.25);
}

.new-client-form {
    .form-row-dual {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 16px;
    }
}

.separator-v3 {
    .line {
        height: 1px;
    }
}

/* Custom Scrollbar for the form */
.new-client-form::-webkit-scrollbar {
    width: 4px;
}
.new-client-form::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 4px;
}
</style>
